import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid, Stack, Container } from '@mui/material';
import { Topheader } from '../../components/Topheader';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import TableV2, { spreadPaginationOptions } from '../../components/Table/TableV2';
import locationsService from '../../services/locationsService';
import { ActivateButton, EditButton } from '../../components/Table/components';

const LocationsTable = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isUserAllowedToExport, setIsUserAllowedToExport] = useState(false);
  const [isUserAllowedToChangeStatus, setIsUserAllowedToChangeStatus] = useState(false);
  const [isUserAllowedToEditLocation, setIsUserAllowedToEditLocation] = useState(false);
  const [messages, setMessages] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [locations, setLocations] = useState([]);
  const [paginationOptions, setPaginationOptions] = useState({});

  useEffect(() => {
    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};
    setIsUserAllowedToExport(user?.role <= 1 || !!user?.permissions?.exportRecord);
    setIsUserAllowedToChangeStatus(user?.role <= 3 || !!user?.permissions?.isActiveLocation);
    setIsUserAllowedToEditLocation(user?.role <= 3 || !!user?.permissions?.editLocation);
  }, []);

  const getLocations = (paginationOptions) => {
    const query = spreadPaginationOptions(paginationOptions);

    setIsLoading(true);
    locationsService
      .getLocations(query)
      .then((response) => {
        if (response.data) {
          setLocations(response.data?.locations ?? []);
          setRowCount(response.data?.rowCount ?? 0);
        }
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not retrieve locations. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchLocations = (newPaginationOptions) => {
    setPaginationOptions(newPaginationOptions);
    getLocations(newPaginationOptions);
  };

  const handleToggleLocationStatus = (locationId, newStatus) => {
    setIsLoading(true);
    locationsService
      .updateLocationStatus(locationId, newStatus)
      .then(() => {
        getLocations(paginationOptions);
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not update location status. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditLocation = (locationId) => {
    history.push('/EditLocations/' + locationId);
  };

  const columns = [
    {
      field: 'locationName',
      type: 'string',
      headerName: 'Location Name',
      minWidth: 250,
      flex: 2,
    },
    {
      field: 'contactName',
      type: 'string',
      minWidth: 200,
      flex: 1,
      headerName: 'Contact Name',
    },
    {
      field: 'city',
      type: 'string',
      minWidth: 130,
      flex: 1,
      headerName: 'City',
    },
    {
      field: 'address1',
      type: 'string',
      minWidth: 200,
      flex: 1,
      headerName: 'Address',
    },
    {
      field: 'createdAt',
      type: 'date',
      headerName: 'Creation Date',
      minWidth: 150,
      flex: 1,
      valueGetter: (value) => new Date(value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={2}>
          {isUserAllowedToEditLocation && (
            <EditButton id={`edit-${params.row._id}`} onClick={() => handleEditLocation(params.row._id)} />
          )}
          {isUserAllowedToChangeStatus && (
            <ActivateButton
              id={`activate-${params.row._id}`}
              onClick={() => handleToggleLocationStatus(params.row._id, !params.row.status)}
              isActive={params.row.status}
            />
          )}
        </Stack>
      ),
    },
  ];

  return (
    <div className="rightPanel">
      <Topheader />
      <div className="contentPart">
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginBottom: '40px' }}
          className="terminal-container term-add"
        >
          <Grid container rowSpacing={2}>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <h3 className="terminal-title">Locations</h3>
            </Grid>
            <Grid item xs={12}>
              <FlexAlerts messages={messages} />
            </Grid>
            <Grid item xs={12}>
              <TableV2
                rows={locations}
                columns={columns}
                fetchData={fetchLocations}
                rowCount={rowCount}
                isLoading={isLoading}
                defaultSortColumn="createdAt"
                isExportable={isUserAllowedToExport}
                searchConfig={{ isSearchBar: true }}
              />
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default LocationsTable;
